/**
 * CoverStudioModal — prompt-driven, photo-capable proposal cover builder.
 *
 * Replaces the old abstract "block" cover flow for the "Create a cover"
 * entry. The operator:
 *   1. picks a style (photo-led or graphic),
 *   2. writes a brief + client/title/subtitle,
 *   3. (photo-led) searches Unsplash/Pexels or uploads a photo,
 *   4. generates a polished A4 cover (Claude lays out HTML → crisp PNG),
 *   5. previews + downloads, or regenerates.
 *
 * Backend: POST /api/v2/cover-design/claude-compose  (no Gamma, no image-gen credits).
 * Stock search reuses GET /api/v2/workspaces/{ws}/stock/search (Unsplash + Pexels).
 */
import React, { useState, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Loader2, Image as ImageIcon, Sparkles, Search, Upload, Download,
  RotateCcw, Check, Layout, FileImage, X,
} from "lucide-react";
import { toast } from "sonner";

const _API_BASE = (typeof window !== "undefined" && window.location.hostname !== "localhost")
  ? window.location.origin
  : (process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : ""));
const API = `${_API_BASE}/api`;

const STYLES = [
  { id: "photo", title: "Photo-led", desc: "A full-bleed photograph with your words set over a tasteful scrim.", icon: FileImage },
  { id: "graphic", title: "Graphic", desc: "An elegant typographic cover — gradients, geometry, brand blues.", icon: Layout },
];

const ORIENTATIONS = [
  { id: "a4_portrait", label: "A4 Portrait" },
  { id: "a4_landscape", label: "A4 Landscape" },
];

export default function CoverStudioModal({ open, onOpenChange, workspaceId }) {
  const [style, setStyle] = useState("photo");
  const [orientation, setOrientation] = useState("a4_portrait");
  const [brief, setBrief] = useState("");
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [clientName, setClientName] = useState("");

  // Image source
  const [imgTab, setImgTab] = useState("search"); // search | upload
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(null); // { full_url, thumb_url, attribution_text, source }
  const [uploadPreview, setUploadPreview] = useState(null); // data URI
  const fileRef = useRef(null);
  const reqRef = useRef(0);

  // Generation / result
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null); // { preview_url, cover_id }
  const [downloading, setDownloading] = useState(false);

  const reset = useCallback(() => {
    setResult(null);
  }, []);

  const resetAll = useCallback(() => {
    reqRef.current += 1; // invalidate any in-flight generation
    setStyle("photo"); setOrientation("a4_portrait");
    setBrief(""); setTitle(""); setSubtitle(""); setClientName("");
    setImgTab("search"); setQuery(""); setCandidates([]); setSelected(null);
    setUploadPreview(null); setResult(null); setGenerating(false);
  }, []);

  const runSearch = async () => {
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    setCandidates([]);
    try {
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/stock/search?q=${encodeURIComponent(q)}&sources=unsplash,pexels&per_page=24`,
        { credentials: "include" }
      );
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(typeof e?.detail === "string" ? e.detail : `Search failed (${r.status})`);
      }
      const body = await r.json();
      setCandidates(body.candidates || []);
      if (!(body.candidates || []).length) toast.message("No photos found — try different words.");
    } catch (e) {
      toast.error(e.message || "Photo search failed");
    } finally {
      setSearching(false);
    }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file (JPG/PNG)."); return; }
    if (f.size > 12 * 1024 * 1024) { toast.error("Image too large (max 12 MB)."); return; }
    const reader = new FileReader();
    reader.onload = () => { setUploadPreview(reader.result); setSelected(null); };
    reader.readAsDataURL(f);
  };

  const chosenPhotoLabel = () => {
    if (imgTab === "upload" && uploadPreview) return "Uploaded photo";
    if (imgTab === "search" && selected) return `${selected.source || "stock"} photo`;
    return null;
  };

  const canGenerate = () => {
    if (generating) return false;
    if (!brief.trim() && !title.trim()) return false;
    return true;
  };

  const generate = async () => {
    const myReq = ++reqRef.current;
    setGenerating(true);
    setResult(null);
    try {
      const payload = {
        brief: brief.trim(),
        style,
        title: title.trim(),
        subtitle: subtitle.trim(),
        client_name: clientName.trim(),
        orientation,
      };
      if (style === "photo") {
        if (imgTab === "upload" && uploadPreview) payload.image_data = uploadPreview;
        else if (imgTab === "search" && selected?.full_url) payload.image_url = selected.full_url;
      }
      const r = await fetch(`${API}/v2/cover-design/claude-compose`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (reqRef.current !== myReq) return; // modal closed/reset or superseded
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(typeof e?.detail === "string" ? e.detail : `Generation failed (${r.status})`);
      }
      const body = await r.json();
      if (reqRef.current !== myReq) return;
      setResult(body);
    } catch (e) {
      if (reqRef.current === myReq) toast.error(e.message || "Cover generation failed");
    } finally {
      if (reqRef.current === myReq) setGenerating(false);
    }
  };

  const download = async () => {
    if (!result?.cover_id) return;
    setDownloading(true);
    try {
      const r = await fetch(`${API}/v2/cover-design/claude-covers/${result.cover_id}.png?download=true`, { credentials: "include" });
      if (!r.ok) throw new Error(`Download failed (${r.status})`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `resiligent_cover_${result.cover_id}.png`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || "Couldn't download the cover");
    } finally {
      setDownloading(false);
    }
  };

  const previewSrc = result ? `${API}/v2/cover-design/claude-covers/${result.cover_id}.png` : null;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) resetAll(); }}>
      <DialogContent
        className="max-w-5xl w-[96vw] max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0"
        data-testid="cover-studio-modal"
      >
        <DialogHeader className="px-6 py-4 border-b border-slate-200">
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <Sparkles className="h-5 w-5 text-indigo-600" /> Create a cover
          </DialogTitle>
          <p className="text-sm text-slate-500">
            Describe the proposal, choose a photo or a graphic look, and Rex composes a client-ready A4 cover.
          </p>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 flex-1 min-h-0">
          {/* ── Left: configuration ─────────────────────────────────── */}
          <div className="overflow-y-auto px-6 py-5 space-y-5 border-r border-slate-200" data-testid="cover-studio-config">
            {/* Style */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Cover type</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {STYLES.map((s) => {
                  const Icon = s.icon;
                  const active = style === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setStyle(s.id)}
                      data-testid={`cover-style-${s.id}`}
                      className={`text-left rounded-lg border p-3 transition ${
                        active ? "border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200" : "border-slate-200 hover:border-slate-300 bg-white"
                      }`}
                    >
                      <div className="flex items-center gap-2 font-semibold text-sm text-slate-800">
                        <Icon className="h-4 w-4 text-indigo-600" />{s.title}
                      </div>
                      <p className="text-[11px] text-slate-500 mt-1 leading-snug">{s.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Brief + words */}
            <div>
              <Label htmlFor="cover-brief" className="text-xs uppercase tracking-wider text-slate-500">
                Brief / client context
              </Label>
              <Textarea
                id="cover-brief"
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                rows={3}
                placeholder="e.g. Resilience strategy proposal for a Lagos-based energy operator. Confident, senior, forward-looking tone."
                className="mt-1.5"
                data-testid="cover-brief-input"
              />
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label htmlFor="cover-title" className="text-xs uppercase tracking-wider text-slate-500">Title</Label>
                <Input id="cover-title" value={title} onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Building Operational Resilience" className="mt-1.5" data-testid="cover-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cover-subtitle" className="text-xs uppercase tracking-wider text-slate-500">Subtitle</Label>
                  <Input id="cover-subtitle" value={subtitle} onChange={(e) => setSubtitle(e.target.value)}
                    placeholder="Optional supporting line" className="mt-1.5" data-testid="cover-subtitle-input" />
                </div>
                <div>
                  <Label htmlFor="cover-client" className="text-xs uppercase tracking-wider text-slate-500">Client</Label>
                  <Input id="cover-client" value={clientName} onChange={(e) => setClientName(e.target.value)}
                    placeholder="Client / company name" className="mt-1.5" data-testid="cover-client-input" />
                </div>
              </div>
            </div>

            {/* Orientation */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Orientation</Label>
              <div className="flex gap-2 mt-2">
                {ORIENTATIONS.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setOrientation(o.id)}
                    data-testid={`cover-orientation-${o.id}`}
                    className={`text-xs rounded-md border px-3 py-1.5 transition ${
                      orientation === o.id ? "border-indigo-500 bg-indigo-50 text-indigo-700 font-semibold" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >{o.label}</button>
                ))}
              </div>
            </div>

            {/* Photo source (photo style only) */}
            {style === "photo" && (
              <div className="border-t border-slate-100 pt-4" data-testid="cover-photo-source">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Photo</Label>
                  {chosenPhotoLabel() && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 font-medium">
                      <Check className="h-3 w-3" /> {chosenPhotoLabel()}
                    </span>
                  )}
                </div>
                <div className="flex gap-1 mt-2 mb-3 bg-slate-100 rounded-md p-0.5 w-fit">
                  <button type="button" onClick={() => setImgTab("search")} data-testid="cover-img-tab-search"
                    className={`text-xs px-3 py-1 rounded ${imgTab === "search" ? "bg-white shadow-sm font-semibold text-slate-800" : "text-slate-500"}`}>
                    <Search className="h-3 w-3 inline mr-1" />Search stock
                  </button>
                  <button type="button" onClick={() => setImgTab("upload")} data-testid="cover-img-tab-upload"
                    className={`text-xs px-3 py-1 rounded ${imgTab === "upload" ? "bg-white shadow-sm font-semibold text-slate-800" : "text-slate-500"}`}>
                    <Upload className="h-3 w-3 inline mr-1" />Upload
                  </button>
                </div>

                {imgTab === "search" ? (
                  <div>
                    <div className="flex gap-2">
                      <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runSearch(); } }}
                        placeholder="Search Unsplash & Pexels — e.g. city skyline at dusk"
                        data-testid="cover-stock-query"
                      />
                      <Button type="button" onClick={runSearch} disabled={searching || !query.trim()} data-testid="cover-stock-search-btn">
                        {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                      </Button>
                    </div>
                    {candidates.length > 0 && (
                      <div className="grid grid-cols-3 gap-2 mt-3 max-h-56 overflow-y-auto pr-1" data-testid="cover-stock-grid">
                        {candidates.map((c) => {
                          const picked = selected?.full_url === c.full_url;
                          return (
                            <button
                              key={`${c.source}-${c.source_id}`}
                              type="button"
                              onClick={() => { setSelected(c); setUploadPreview(null); }}
                              data-testid={`cover-stock-pick-${c.source}-${c.source_id}`}
                              className={`relative rounded-md overflow-hidden border-2 aspect-[4/5] ${picked ? "border-indigo-500 ring-2 ring-indigo-200" : "border-transparent hover:border-slate-300"}`}
                              title={c.attribution_text || c.description || ""}
                            >
                              <img src={c.thumb_url} alt={c.description || "stock"} className="w-full h-full object-cover" loading="lazy" />
                              {picked && (
                                <span className="absolute top-1 right-1 bg-indigo-600 text-white rounded-full p-0.5"><Check className="h-3 w-3" /></span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {selected?.attribution_text && (
                      <p className="text-[10px] text-slate-400 mt-2">{selected.attribution_text}</p>
                    )}
                  </div>
                ) : (
                  <div>
                    <input ref={fileRef} type="file" accept="image/*" onChange={onPickFile} className="hidden" data-testid="cover-upload-input" />
                    {uploadPreview ? (
                      <div className="relative inline-block">
                        <img src={uploadPreview} alt="upload" className="h-40 rounded-md object-cover border border-slate-200" />
                        <button type="button" onClick={() => { setUploadPreview(null); if (fileRef.current) fileRef.current.value = ""; }}
                          className="absolute -top-2 -right-2 bg-white border border-slate-300 rounded-full p-1 shadow-sm" data-testid="cover-upload-clear">
                          <X className="h-3 w-3 text-slate-600" />
                        </button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => fileRef.current?.click()} data-testid="cover-upload-btn"
                        className="w-full border-2 border-dashed border-slate-300 rounded-lg py-8 flex flex-col items-center gap-2 text-slate-500 hover:border-indigo-400 hover:text-indigo-600 transition">
                        <Upload className="h-6 w-6" />
                        <span className="text-xs">Click to upload a photo (JPG/PNG, max 12 MB)</span>
                      </button>
                    )}
                  </div>
                )}
                <p className="text-[11px] text-slate-400 mt-2">
                  No photo? Rex will still design a strong photo-style cover from the brief.
                </p>
              </div>
            )}

            <Button
              onClick={generate}
              disabled={!canGenerate()}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="cover-generate-btn"
            >
              {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Composing cover…</> : <><Sparkles className="h-4 w-4 mr-2" />Generate cover</>}
            </Button>
          </div>

          {/* ── Right: preview / result ─────────────────────────────── */}
          <div className="overflow-y-auto px-6 py-5 bg-slate-50 flex flex-col items-center justify-center" data-testid="cover-studio-preview">
            {generating ? (
              <div className="flex flex-col items-center gap-3 text-slate-500">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
                <p className="text-sm">Rex is designing your cover…</p>
                <p className="text-xs text-slate-400">Typically 20–40 seconds</p>
              </div>
            ) : result ? (
              <div className="w-full flex flex-col items-center gap-4">
                <img
                  src={previewSrc}
                  alt="cover preview"
                  data-testid="cover-result-img"
                  className="max-h-[62vh] w-auto rounded-md shadow-lg border border-slate-200 bg-white"
                />
                <div className="flex gap-2">
                  <Button onClick={download} disabled={downloading} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cover-download-btn">
                    {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Download PNG
                  </Button>
                  <Button variant="outline" onClick={() => { reset(); generate(); }} data-testid="cover-regenerate-btn">
                    <RotateCcw className="h-4 w-4 mr-2" />Regenerate
                  </Button>
                </div>
                <p className="text-[11px] text-slate-400">Not quite right? Tweak the brief or photo on the left and regenerate.</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3 text-slate-400 text-center">
                <ImageIcon className="h-12 w-12" />
                <p className="text-sm max-w-xs">Your composed cover will appear here. Fill in the brief, pick a photo if you like, then hit <span className="font-semibold text-slate-600">Generate cover</span>.</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
